import type { ReactNode } from 'react';

import { MoneyCell } from '@/components/primitives';
import { performanceMetricValue } from '@/lib/performance-metrics';
import type {
  DirectPerformanceMetrics,
  PerformanceMetricId,
} from '@/lib/performance-metrics';

/** Provider-attributed tickets keep their modeled fractions, as Reports shows them. */
export function formatAttributedCount(value: number): string {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 6,
  });
}

/**
 * One Performance metric as Reports renders it. Campaigns renders its sales
 * through this too, so a figure on either page reads the same.
 */
export function performanceMetricCell(
  metrics: DirectPerformanceMetrics,
  metric: PerformanceMetricId,
): ReactNode {
  const value = performanceMetricValue(metrics, metric);
  if (value === null) return <span className="dim" title="Not available for a zero denominator">—</span>;
  if (metric === 'gross_spend' || metric === 'attributed_revenue') {
    return <MoneyCell value={value} dp={2}/>;
  }
  if (metric === 'cpm' || metric === 'cpc' || metric === 'cpa') {
    return <MoneyCell value={value} dp={2}/>;
  }
  if (metric === 'ctr') return `${value.toFixed(2)}%`;
  if (metric === 'roas') return `${value.toFixed(2)}×`;
  if (metric === 'attributed_conversions') return formatAttributedCount(value);
  return Math.trunc(value).toLocaleString('en-US');
}
